<script setup lang="ts">
import { computed, nextTick, Ref, ref } from "vue";
import { Line, Scatter } from "vue-chartjs";
import { ChartData, ChartDataset, ChartOptions, Point } from "chart.js";
import dragData from "chartjs-plugin-dragdata";
import { useHandlePreviewPlugin } from "@/modules/booking_experts/pages/Components/Chart/HandlePlugin";
import OffsetChartHeader from "@/modules/booking_experts/pages/Components/Chart/OffsetChartHeader.vue";
import OffsetCurveItemFooter from "@/modules/booking_experts/pages/Components/Chart/OffsetCurveItemFooter.vue";
import {
	useCurveModelChartStore,
	useCurveModelStore,
} from "@/modules/booking_experts/store";
import PeriodSelectionDTO = App.Data.Explorer.PeriodSelectionDTO;
import dayjs from "dayjs";

/* Constants */
const CLICK_TIMEOUT = 250;
const DEFAULT_ROUNDING = 1;
const POINT_SIZE = 5;
const MIN_POINT_DISTANCE = DEFAULT_ROUNDING / 10;
const BAR_WIDTH = 2;

/* Props */
const {
	showFooter = true,
	showHeader = true,
	isReadonly = false,
	showBorder = true,
} = defineProps<{
	/** Show the header */
	showHeader?: boolean;
	/** Is this chart somehow editable? */
	isReadonly?: boolean;
	/** Show the offset edit footer */
	showFooter?: boolean;
	/** Show a rounded border around the graph */
	showBorder?: boolean;
}>();

const store = useCurveModelStore();
const chartStore = useCurveModelChartStore();
defineEmits<{
	(
		e: "updateBaseData",
		data: { priceListId: number; period: PeriodSelectionDTO }
	): void;
}>();

const clickCount = ref(0);
const clickTimeout = ref<ReturnType<typeof setTimeout>>();
const handlePoint = ref<Point | null>(null);

/* Build array of labels for the X axis */
const labels = Array.from(
	{ length: store.currentCurve.days },
	(_, i) => i
).reverse();

/* Some computed booleans to tweak chart rendering or content */
const hasPYData = computed(() => store.referenceData.y1.length > 0);
const hasCYData = computed(() => store.referenceData.y0.length > 0);
const isHandleRequired = computed(() => store.currentCurve.type === "custom");

/* Plugin for showing a handle preview on the chart */
const handlePreviewPlugin = useHandlePreviewPlugin({
	handlePoint: handlePoint,
	active: isHandleRequired,
});

/**
 * Return an opacity based on index or a highlighted index.
 */
function calculateOpacity(
	index: number,
	totalCount: number,
	highlightedIndex: number | null
): number {
	if (highlightedIndex !== null) {
		return index === highlightedIndex ? 0.7 : 0.1;
	}
	return 0.5 + (0.2 - 0.5) * (1 - index / Math.max(1, totalCount));
}

/**
 * Construct a dataset with default overrides.
 */
function createDataset(
	data: Point[],
	config: Partial<ChartDataset<"line">>
): ChartDataset<"line"> {
	return {
		type: "line",
		borderWidth: 2,
		pointRadius: 0,
		fill: false,
		dragData: false,
		...config,
		data,
	};
}

/**
 * Full chart data object
 */
const data = computed<ChartData<"line">>(() => {
	const datasets: ChartDataset<"line">[] = [];
	if (hasPYData.value || hasCYData.value) {
		if (store.showCY) {
			// NOTE: Array is reversed.
			const sliceFrom =
				store.currentOccupancy === null
					? 0
					: Math.max(store.currentOccupancy.leadDay, 0);

			const sliceTo = store.currentCurve.days;
			datasets.push(
				createDataset(store.referenceData.y0.slice(sliceFrom, sliceTo), {
					label: "Y",
					borderColor: "#667992",
					pointHitRadius: 2,
				})
			);
		}
		if (store.showPY) {
			datasets.push(
				createDataset(store.referenceData.y1, {
					label: "Y-1",
					borderColor: "#76ade1",
					pointHitRadius: 2,
				})
			);
		}
	}

	if (store.currentOccupancy !== null) {
		datasets.push(
			createDataset(
				[
					{
						x: Math.max(store.currentOccupancy.leadDay, 0),
						y: store.currentOccupancy.occupancy,
					},
				],
				{
					backgroundColor: "rgb(255,247,0)",
					borderDash: [5, 5],
					order: -1,
					label: "Occupancy",
					type: "scatter",
					pointRadius: 4,
				}
			)
		);

		// NOTE(Leon): See APP-363
		// datasets.push(
		// 	createDataset(
		// 		[
		// 			{
		// 				x: store.currentOccupancy.bookedLeadDay,
		// 				y: store.currentOccupancy.bookedOccupancy,
		// 			},
		// 		],
		// 		{
		// 			backgroundColor: "rgb(238,160,0)",
		// 			borderDash: [5, 5],
		// 			order: -1,
		// 			label: "Occupancy",
		// 			type: "scatter",
		// 			pointRadius: 4,
		// 		}
		// 	)
		// );
	}

	// Positive offsets
	let previousData = store.currentCurve.base_dataset;
	store.currentCurve.offsets
		.filter((o) => o.offsetPercentage > 0)
		.forEach((offset, index, arr) => {
			const sourceData =
				index === 0 ? store.currentCurve.base_dataset : previousData;
			const newData = sourceData.map((point) => ({
				x: point.x,
				y: point.y + offset.offsetPercentage,
			}));

			const opacity = calculateOpacity(
				index,
				arr.length,
				chartStore.highlightedPositiveIndex
			);
			datasets.unshift(
				createDataset(newData, {
					label: `+${index}%`,
					backgroundColor: `rgba(0, 173, 0, ${opacity})`,
					borderColor: `rgba(0, 173, 0, ${opacity})`,
					fill: index === arr.length - 1 ? { value: 100 } : { target: "-1" },
				})
			);
			previousData = newData;
		});

	// Base dataset
	datasets.push(
		createDataset(store.currentCurve.base_dataset, {
			label: "Base Data",
			borderColor: "rgb(168,168,168)",
			dragData: store.currentCurve.type === "custom" && !isReadonly,
			pointRadius: isHandleRequired.value && !isReadonly ? POINT_SIZE : 0,
			pointHitRadius: isHandleRequired.value && !isReadonly ? POINT_SIZE : 0,
		})
	);

	// Negative offsets
	let cumulativeNegative = 0;
	store.currentCurve.offsets
		.filter((o) => o.offsetPercentage < 0)
		.forEach((offset, index, arr) => {
			cumulativeNegative += offset.offsetPercentage;
			const opacity = calculateOpacity(
				index,
				arr.length,
				chartStore.highlightedNegativeIndex
			);
			datasets.push(
				createDataset(
					store.currentCurve.base_dataset.map((point) => ({
						x: point.x,
						y: point.y + cumulativeNegative,
					})),
					{
						label: `${cumulativeNegative}%`,
						backgroundColor: `rgba(173, 0, 0, ${opacity})`,
						borderColor: `rgba(173, 0, 0, ${opacity})`,
						fill:
							index === arr.length - 1
								? { value: 0 }
								: { target: datasets.length + 1 },
					}
				)
			);
		});

	return { labels, datasets };
});

/**
 * Check if the pointer is inside the chart area.
 * Return false if the chart instance or chartArea is missing.
 */
function isInChartArea(e: MouseEvent) {
	return (e.target as HTMLModElement).nodeName === "CANVAS";
}

/**
 * Called whenever the user lets go after dragging a point.
 * Resets the X and Y of dragData, so the points don't get stuck when then user goes too far out of bounds.
 */
function handleDragEnd() {
	//@ts-ignore -- I don't know the type interface for the plugin
	options.value.plugins.dragData.dragX = true;
	//@ts-ignore
	options.value.plugins.dragData.dragY = true;
}

/**
 * Called whenever the user drags a point. If the user moves outside the chart, skip updates.
 */
function handleDrag(
	e: MouseEvent,
	datasetIndex: number,
	index: number,
	value: Point | number
) {
	const allowDrag = isInChartArea(e);
	//@ts-ignore -- I don't know the type interface for the plugin
	options.value.plugins.dragData.dragX = allowDrag;
	//@ts-ignore
	options.value.plugins.dragData.dragY = allowDrag;

	if (typeof value === "number") {
		throw new Error("Value must be a Point object, not a number!");
	}

	const dataset = data.value.datasets[datasetIndex].data;
	if (store.moveDirection === "vertical") {
		// Only allow vertical movement; keep X fixed
		const deltaY = value.y - (dataset[index] as Point).y;
		value.x = (dataset[index] as Point).x;

		dataset.forEach((_point, i) => {
			(dataset[i] as Point).y = Math.min(
				100,
				Math.max(0, (dataset[i] as Point).y + deltaY)
			);
		});
		return;
	}

	// For horizontal + vertical:
	value.x = Math.round(value.x);

	// Keep the first/last points anchored horizontally
	if (index === 0 || index === dataset.length - 1) {
		value.x = (dataset[index] as Point).x;
	} else {
		// Constrain horizontal so it doesn't overlap neighboring points
		const [before, after] = [
			dataset[index - 1] as Point,
			dataset[index + 1] as Point,
		];

		value.x = Math.min(after.x - 1, Math.max(before.x + 1, value.x));
	}

	// Keep Y in [0, 100]
	value.y = Math.min(100, Math.max(0, value.y));
}

/**
 * If user double-clicks on the chart with a handle preview, we add a point.
 * If user shift-clicks on an existing point, remove it (excluding first/last).
 */
function handleChartClick(event: any, elements: any[], chart: any) {
	if (event.native.shiftKey) {
		handlePointRemoval(elements, chart);
		return;
	}

	clickCount.value++;
	if (clickCount.value === 1) {
		clickTimeout.value = setTimeout(
			() => (clickCount.value = 0),
			CLICK_TIMEOUT
		);
		return;
	}

	// Double-click logic
	if (clickCount.value === 2 && handlePoint.value) {
		clearTimeout(clickTimeout.value);
		clickCount.value = 0;
		addNewPoint(chart);
	}
}

function addNewPoint(chart: any) {
	if (!handlePoint.value) return;

	const newX = Math.round(handlePoint.value.x);
	const newY = handlePoint.value.y;
	handlePoint.value = null;

	const isTooClose = store.currentCurve.base_dataset.some(
		(point) => Math.abs(point.x - newX) < MIN_POINT_DISTANCE
	);
	if (isTooClose) return;

	const insertIndex = store.currentCurve.base_dataset.findIndex(
		(point) => point.x > newX
	);
	const actualIndex =
		insertIndex === -1 ? store.currentCurve.base_dataset.length : insertIndex;

	const newDataset = [...store.currentCurve.base_dataset];
	newDataset.splice(actualIndex, 0, { x: newX, y: newY });
	store.currentCurve = { ...store.currentCurve, base_dataset: newDataset };

	nextTick(() => chart.update());
}

/**
 * Remove a point when shift-clicking an existing point (except first/last).
 */
function handlePointRemoval(elements: any[], chart: any) {
	if (elements.length === 0) return;
	const [element] = elements;

	// The base dataset is roughly in the middle of the dataset array (positive offsets prepended)
	const baseDatasetIndex = data.value.datasets.findIndex(
		(ds) => ds.label === "Base Data"
	);

	if (
		element.datasetIndex !== baseDatasetIndex ||
		element.index === 0 ||
		element.index === store.currentCurve.base_dataset.length - 1
	) {
		return;
	}

	const newDataset = [...store.currentCurve.base_dataset];
	newDataset.splice(element.index, 1);
	store.currentCurve = { ...store.currentCurve, base_dataset: newDataset };

	nextTick(() => chart.update());
}

/**
 * Chart options that enable drag, plus custom constraints.
 */
const options: Ref<ChartOptions<"line">> = ref({
	animation: false,
	spanGaps: false,
	onClick: handleChartClick,
	plugins: {
		legend: { display: false },
		filler: { propagate: false },
		dragData: {
			dragX: true,
			dragY: true,
			round: DEFAULT_ROUNDING,
			showTooltip: true,
			onDrag: handleDrag,
			onDragEnd: handleDragEnd,
		},
		tooltip: { enabled: true },
	},
	scales: {
		x: {
			title: { display: true, text: "Lead days" },
			type: "linear",
			reverse: true,
			ticks: { stepSize: 1 },
		},
		y: {
			title: { display: true, text: "Occupancy (%)" },
			type: "linear",
			position: "left",
			max: 100,
			min: 0,
		},
	},
});
</script>

<template>
	<v-card class="pa-4">
		<div class="mb-4" v-if="showHeader">
			<OffsetChartHeader />
		</div>
		<Line
			ref="chart"
			:class="showBorder ? 'border rounded pa-2' : 'ma-2'"
			:data="data"
			:options="options"
			:plugins="[dragData, handlePreviewPlugin]"
		/>
		<div class="mt-4" v-if="showFooter">
			<OffsetCurveItemFooter :isReadonly="isReadonly" />
		</div>
	</v-card>
</template>
